import React, { useState, useCallback, useEffect } from "react";
import classNames from "classnames";
import type { SettingsSectionProps } from "./types";
import { useOrganizationSettings, useEmployees } from "@/hooks/data";
import { Icon } from "@/components/ui/display/Icon";
import { FlashAlert } from "@/components/ui/feedback/FlashAlert";
import { StatusMessages } from "@/components/ui/feedback/StatusMessages";
import { Tooltip } from "@/components/ui/feedback/Tooltip";
import { IconButton } from "@/components/ui/buttons/IconButton";
import { SectionHeader } from "@/components/ui/navigation/SectionHeader";
import { OrgSettingsForm } from "./OrgSettingsForm";
import { EmployeeSettingsList } from "./EmployeeSettingsList";
import layoutStyles from "@/components/admin/AdminSectionLayout.module.css";
import styles from "./SettingsSection.module.css";
import type { IServiceEmployee } from "@/services/types";
import { EmployeeSettingsModal } from "./EmployeeSettingsModal";

/** Explanation text for settings section */
const SETTINGS_EXPLANATION =
  "Denne seksjonen lar deg tilpasse organisasjonsinnstillinger og avgjøre hvilke ansatte som skal være bookbare i systemet.";

/**
 * SettingsSection - Manage organization and employee settings
 * Part of the Admin page
 */
export const SettingsSection: React.FC<SettingsSectionProps> = ({
  className,
  "data-testid": testId = "settings-section",
}) => {
  const {
    settings,
    isLoading: settingsLoading,
    error: settingsError,
    updateSettings,
    isUpdating,
  } = useOrganizationSettings();

  const {
    activeEmployees,
    isLoading: employeesLoading,
    error: employeesError,
    updateEmployeeSettings,
    isUpdatingSettings,
  } = useEmployees();

  // Local state for organization settings form
  const [hoursInDay, setHoursInDay] = useState<number>(7.5);
  const [disableFravaer, setDisableFravaer] = useState<boolean>(false);
  const [includePublicHolidays, setIncludePublicHolidays] =
    useState<boolean>(true);
  const [includeWeekends, setIncludeWeekends] = useState<boolean>(false);

  // State for flash alerts
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // State for mobile help tooltip (only shown on mobile)
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);

  const [selectedEmployee, setSelectedEmployee] =
    useState<IServiceEmployee | null>(null);

  // Sync local state with fetched settings
  useEffect(() => {
    if (settings) {
      setHoursInDay(settings.hours_in_day);
      setDisableFravaer(settings.disable_fravaer);
      // Use defaults if API doesn't return these yet
      setIncludePublicHolidays(settings.include_holidays ?? true);
      setIncludeWeekends(settings.include_weekends ?? false);
    }
  }, [settings]);

  // Handle organization settings save
  const handleSaveOrgSettings = useCallback(async () => {
    const result = await updateSettings({
      hours_in_day: hoursInDay,
      disable_fravaer: disableFravaer,
      include_holidays: includePublicHolidays,
      include_weekends: includeWeekends,
    });

    if (result) {
      setSuccessMessage("Organisasjonsinnstillinger lagret");
    } else {
      setErrorMessage("Kunne ikke lagre organisasjonsinnstillinger");
    }
  }, [
    hoursInDay,
    disableFravaer,
    includePublicHolidays,
    includeWeekends,
    updateSettings,
  ]);

  // Handle employee bookable toggle
  const handleBookableToggle = useCallback(
    async (employeeId: number, isBookable: boolean) => {
      const result = await updateEmployeeSettings(employeeId, {
        is_bookable: isBookable,
      });

      if (result) {
        setSuccessMessage("Ansattinnstilling oppdatert");
      } else {
        setErrorMessage("Kunne ikke oppdatere ansattinnstilling");
      }
    },
    [updateEmployeeSettings],
  );

  return (
    <div
      className={classNames(layoutStyles.section, styles.section, className)}
      data-testid={testId}
    >
      {/* Explanation text - visible only on desktop */}
      <p className={layoutStyles.explanationText}>{SETTINGS_EXPLANATION}</p>

      {/* Flash alerts */}
      {successMessage && (
        <FlashAlert
          alert={{ message: successMessage, variant: "success" }}
          onDismiss={() => setSuccessMessage(null)}
          data-testid={`${testId}-success-alert`}
        />
      )}
      {errorMessage && (
        <FlashAlert
          alert={{ message: errorMessage, variant: "error" }}
          onDismiss={() => setErrorMessage(null)}
          data-testid={`${testId}-error-alert`}
        />
      )}

      <div className={layoutStyles.dataAreaGrid}>
        <section
          className={classNames(layoutStyles.card, styles.formSection)}
          data-area="org-settings"
          data-testid={`${testId}-org-settings`}
        >
          <SectionHeader
            as="h3"
            size="md"
            action={
              <div className={styles.helpButtonWrapper}>
                <Tooltip
                  content={SETTINGS_EXPLANATION}
                  position="left"
                  width="md"
                  show={showHelpTooltip}
                >
                  <IconButton
                    icon={<Icon name="help-circle" size="sm" aria-hidden />}
                    variant="default"
                    size="sm"
                    onClick={() => setShowHelpTooltip((prev) => !prev)}
                    aria-label="Hva kan jeg gjøre her?"
                    className={styles.helpButton}
                    data-testid={`${testId}-help-button`}
                  />
                </Tooltip>
              </div>
            }
          >
            Organisasjonsinnstillinger
          </SectionHeader>

          <StatusMessages
            error={settingsError}
            isLoading={settingsLoading}
            errorLabel="Kunne ikke laste innstillinger"
            loadingLabel="Laster innstillinger..."
            testId={`${testId}-org-status`}
          />

          {!settingsLoading && !settingsError && (
            <OrgSettingsForm
              hoursInDay={hoursInDay}
              onHoursChange={setHoursInDay}
              disableFravaer={disableFravaer}
              onDisableFravaerChange={setDisableFravaer}
              includePublicHolidays={includePublicHolidays}
              onIncludeHolidaysChange={setIncludePublicHolidays}
              includeWeekends={includeWeekends}
              onIncludeWeekendsChange={setIncludeWeekends}
              isUpdating={isUpdating}
              onSave={handleSaveOrgSettings}
              testId={testId}
            />
          )}
        </section>

        <section
          className={classNames(layoutStyles.card, styles.listSection)}
          data-area="employee-settings"
          data-testid={`${testId}-employee-settings`}
        >
          <SectionHeader as="h3" size="md">
            Ansatt innstillinger
          </SectionHeader>

          <StatusMessages
            error={employeesError}
            isLoading={employeesLoading}
            errorLabel="Kunne ikke laste ansatte"
            loadingLabel="Laster ansatte..."
            testId={`${testId}-employee-status`}
          />

          {!employeesLoading && !employeesError && (
            <EmployeeSettingsList
              employees={activeEmployees}
              isUpdating={isUpdatingSettings}
              onOpenEmployeeSettings={(employee) =>
                setSelectedEmployee(employee)
              }
              testId={testId}
            />
          )}
        </section>

        {selectedEmployee && (
          <EmployeeSettingsModal
            employee={selectedEmployee}
            isUpdating={isUpdatingSettings}
            onClose={() => setSelectedEmployee(null)}
            onToggleBookable={(checked) =>
              handleBookableToggle(selectedEmployee.id, checked)
            }
            testId={`${testId}-employee-settings-modal`}
          />
        )}
      </div>
    </div>
  );
};

SettingsSection.displayName = "SettingsSection";

export default SettingsSection;
